import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, FormControlLabel } from '@mui/material';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import { RadioV2 } from '@/components/@IntranetLibrary/Radio/RadioV2';
import Select from '@/components/@IntranetLibrary/Select';
import { useSnackbar } from '@/hooks/global-tools';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import {
  ApprovalFormTable,
  ApprovalOutlineInput,
  ApprovalTableCellInput,
  ApprovalTableRowDeleteButton,
  approvalTableCellSelectSx,
} from '@/components/Approval/@components/ApprovalFormTable';
import {
  BpCellStack,
  BpGuideText,
  BpIndentedField,
  BpInfoTable,
  BpInterestGuide,
  BpRadioRow,
  BpSelectionGuide,
  bpReasonLabel,
} from '@/components/Approval/@components/BusinessPartnerRegistration';
import ReferrerField from '@/components/Approval/@components/ReferrerField';
import { useUserSearch } from '@/components/Approval/@components/ReferrerField/hooks/useUserSearch';
import type { Referrer } from '@/components/Approval/@components/ReferrerField/types';
import {
  BP_CYCLES,
  BP_DUPLICATE_PARTICIPANT_MESSAGE,
  BP_RELATIONS,
  BP_REQUEST_TYPES,
  BP_SELECTION_METHOD_LABEL,
  BP_SOURCING_PATH_LABEL,
  bpAmountLabel,
  bpAmountSpec,
  bpInterestDetailLabels,
  createBpParticipant,
  removeBpParticipant,
  type BpFormState,
  type BpParticipant,
  type BpRelation,
  type BpSourcingPath,
  type BpFormType,
} from '@/utils/approval/businessPartnerRegistration';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

// RadioGroupV2 와 같게 — MUI 기본 9px 여백 제거
const radioLabelSx = {
  margin: 0,
  gap: '8px',
  '& .MuiButtonBase-root': { padding: 0 },
};

type OptionRadioProps = {
  checked: boolean;
  label: string;
  onSelect: () => void;
};

const OptionRadio = ({ checked, label, onSelect }: OptionRadioProps) => (
  <FormControlLabel
    sx={radioLabelSx}
    control={<RadioV2 checked={checked} onChange={onSelect} />}
    label={label}
  />
);

/** 이해관계 상세 표 — 체크·소속·성명·직급·이해관계자 성명·직함·관계 (비고는 나머지). PC 는 비율, 모바일은 px */
const DETAIL_COLS: [mobile: number, pc: string][] = [
  [44, '44px'],
  [110, '14%'],
  [80, '11%'],
  [70, '9%'],
  [100, '13%'],
  [90, '11%'],
  [160, '22%'],
];

const DETAIL_TABLE_STYLE = {
  '--bp-fixed-width': `${DETAIL_COLS.reduce((a, [px]) => a + px, 0)}px`,
} as CSSProperties;

const SOURCING_INPUTS: {
  path: Exclude<BpSourcingPath, '' | 'internet'>;
  field: 'sourcingEmployee' | 'sourcingPartner' | 'sourcingOther';
  placeholder?: string;
}[] = [
  {
    path: 'employee',
    field: 'sourcingEmployee',
    placeholder: '경영개선팀 홍길동 대리',
  },
  {
    path: 'partner',
    field: 'sourcingPartner',
    placeholder: '협력사명 & 추천인 성명/직함 기입',
  },
  { path: 'other', field: 'sourcingOther' },
];

const SOURCING_ORDER: Exclude<BpSourcingPath, ''>[] = [
  'employee',
  'partner',
  'internet',
  'other',
];

const toParticipant = (r: Referrer) =>
  createBpParticipant({
    id: r.id,
    department: r.organizationDisplayName || r.departmentName || '',
    name: r.name,
    position: r.positionName ?? '',
  });

type BusinessPartnerRegistrationFieldProps = {
  apvType: BpFormType;
  /** 회계거래처(101)는 신청유형을 SAP 가 정해 생성/변경 라디오가 없음 */
  hideRequestType?: boolean;
};

/** 거래처 신규/변경 [F&F HK](200)·[F&F TW](201) 본문 — 레거시 appr_edit_forms_eas_hk_bp·eas_tw_bp. 101 은 업체소싱정보 표만 */
const BusinessPartnerRegistrationField = ({
  apvType,
  hideRequestType = false,
}: BusinessPartnerRegistrationFieldProps) => {
  const { t } = useTranslation();
  const amountSpec = bpAmountSpec(apvType);
  const detailLabels = bpInterestDetailLabels(apvType);
  const snackbar = useSnackbar();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'bpRegistration' });
  const [keyword, setKeyword] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  if (!state) return null;

  const commit = (next: BpFormState) =>
    setValue('bpRegistration', next, { shouldDirty: true });

  const update = (patch: Partial<BpFormState>) => {
    const current = getValues('bpRegistration');
    if (current) commit({ ...current, ...patch });
  };

  const updateParticipant = (id: string, patch: Partial<BpParticipant>) => {
    const current = getValues('bpRegistration');
    if (!current) return;
    commit({
      ...current,
      participants: current.participants.map((p) =>
        p.id === id ? { ...p, ...patch } : p,
      ),
    });
  };

  const handlePick = (next: Referrer[]) => {
    setKeyword('');
    const picked = next[next.length - 1];
    const current = getValues('bpRegistration');
    if (!picked || !current) return;
    if (current.participants.some((p) => p.id === picked.id)) {
      snackbar.showError({ message: t(BP_DUPLICATE_PARTICIPANT_MESSAGE) });
      return;
    }
    commit({
      ...current,
      participants: [...current.participants, toParticipant(picked)],
    });
  };

  const handleRemove = (id: string) => {
    const current = getValues('bpRegistration');
    if (current) commit(removeBpParticipant(current, id));
  };

  const hasParticipants = state.participants.length > 0;

  return (
    <FieldBlock>
      {!hideRequestType && (
        <BpRadioRow sx={{ mb: 1.5 }}>
          {BP_REQUEST_TYPES.map((type) => (
            <OptionRadio
              key={type.value}
              checked={state.requestType === type.value}
              label={t(type.label)}
              onSelect={() => update({ requestType: type.value })}
            />
          ))}
        </BpRadioRow>
      )}

      {state.requestType === 'register' && (
        <BpInfoTable>
          <tbody>
            <tr>
              <th>{t('업체 선정 방식')}</th>
              <td>
                <ApprovalNoticeBox sx={{ mb: 1.5 }}>
                  <BpSelectionGuide t={t} />
                </ApprovalNoticeBox>
                <BpCellStack>
                  <OptionRadio
                    checked={state.selectionMethod === 'bidding'}
                    label={t(BP_SELECTION_METHOD_LABEL.bidding)}
                    onSelect={() => update({ selectionMethod: 'bidding' })}
                  />
                  <BpIndentedField>
                    <ApprovalOutlineInput
                      value={state.biddingVendors}
                      disabled={state.selectionMethod !== 'bidding'}
                      placeholder={t('경쟁입찰 참여업체명 기입')}
                      onChange={(e) =>
                        update({ biddingVendors: e.target.value })
                      }
                    />
                  </BpIndentedField>
                  <OptionRadio
                    checked={state.selectionMethod === 'direct'}
                    label={t(BP_SELECTION_METHOD_LABEL.direct)}
                    onSelect={() => update({ selectionMethod: 'direct' })}
                  />
                </BpCellStack>
              </td>
            </tr>
            <tr>
              <th>{t('업체 선정 사유')}</th>
              <td>
                <BpGuideText sx={{ color: 'inherit', fontSize: 14 }}>
                  {t(bpReasonLabel(state))}
                </BpGuideText>
                <ApprovalOutlineInput
                  value={state.selectionReason}
                  onChange={(e) => update({ selectionReason: e.target.value })}
                />
              </td>
            </tr>
            <tr>
              <th>{t('예상 거래 주기')}</th>
              <td>
                <BpGuideText>
                  {t(
                    '연간 예상 거래 주기 체크 (개별 프로젝트 단위로 횟수 카운팅)',
                  )}
                </BpGuideText>
                <BpRadioRow>
                  {BP_CYCLES.map((c) => (
                    <OptionRadio
                      key={c.value}
                      checked={state.cycle === c.value}
                      label={t(c.label)}
                      onSelect={() => update({ cycle: c.value })}
                    />
                  ))}
                </BpRadioRow>
              </td>
            </tr>
            <tr>
              <th>{t('예상 거래 금액 (연간)')}</th>
              <td>
                <BpGuideText>
                  {t(
                    '연간 예상 거래금액 체크 (1회 예상 거래금액 X 연간 예상 거래횟수)',
                  )}
                </BpGuideText>
                <BpCellStack>
                  {amountSpec.options.map((a) => (
                    <OptionRadio
                      key={a.value}
                      checked={state.amount === a.value}
                      label={bpAmountLabel(a, amountSpec.currency, t)}
                      onSelect={() => update({ amount: a.value })}
                    />
                  ))}
                </BpCellStack>
              </td>
            </tr>
            <tr>
              <th>{t('업체 소싱 경로')}</th>
              <td>
                <BpCellStack>
                  {SOURCING_ORDER.map((path) => {
                    const input = SOURCING_INPUTS.find((i) => i.path === path);
                    return (
                      <Box key={path}>
                        <OptionRadio
                          checked={state.sourcingPath === path}
                          label={t(BP_SOURCING_PATH_LABEL[path])}
                          onSelect={() => update({ sourcingPath: path })}
                        />
                        {input && (
                          <BpIndentedField sx={{ mt: 1 }}>
                            <ApprovalOutlineInput
                              value={state[input.field]}
                              disabled={state.sourcingPath !== path}
                              placeholder={
                                input.placeholder ? t(input.placeholder) : ''
                              }
                              onChange={(e) =>
                                update({ [input.field]: e.target.value })
                              }
                            />
                          </BpIndentedField>
                        )}
                      </Box>
                    );
                  })}
                </BpCellStack>
              </td>
            </tr>
            <tr>
              <th>{t('업체 선정(평가) 과정 참여자')}</th>
              <td>
                <BpGuideText>
                  {t(
                    '해당 업체 선정 과정(업체 소싱, 평가, 계약조건 협의 등)에 참여한 모든 임직원 기재',
                  )}
                </BpGuideText>
                <ReferrerField
                  selected={[]}
                  onChange={handlePick}
                  keyword={keyword}
                  onKeywordChange={setKeyword}
                  open={searchOpen}
                  onOpenChange={setSearchOpen}
                  useUserSearch={useUserSearch}
                  allowSelectAll={false}
                  placeholder={t('참여자를 선택하세요.')}
                  showReset={false}
                  hideHeader
                  hideFooter
                />
                {hasParticipants && (
                  <Box sx={{ mt: 1 }}>
                    <ApprovalFormTable className="bp-fit">
                      <colgroup>
                        <col />
                        <col />
                        <col style={{ width: '16%' }} />
                        <col style={{ width: 48 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>{t('소속')}</th>
                          <th>{t('성명')}</th>
                          <th>{t('직급')}</th>
                          <th aria-label={t('삭제')} />
                        </tr>
                      </thead>
                      <tbody>
                        {state.participants.map((p) => (
                          <tr key={p.id}>
                            <td className="align-center">{p.department}</td>
                            <td className="align-center">{p.name}</td>
                            <td className="align-center">{p.position}</td>
                            <td className="align-center">
                              <ApprovalTableRowDeleteButton
                                type="button"
                                aria-label={t('삭제')}
                                onClick={() => handleRemove(p.id)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </ApprovalFormTable>
                  </Box>
                )}
              </td>
            </tr>
            {hasParticipants && (
              <tr>
                <th>{t('이해관계 여부')}</th>
                <td>
                  <ApprovalNoticeBox sx={{ mb: 1.5 }}>
                    <BpInterestGuide t={t} />
                  </ApprovalNoticeBox>
                  <BpGuideText>
                    {t(
                      '업체선정(평가) 과정에 참여한 임직원과 선정된 업체와의 이해관계 여부 체크',
                    )}
                  </BpGuideText>
                  <BpRadioRow>
                    <OptionRadio
                      checked={state.interest === 'no'}
                      label={t('이해관계 없음')}
                      onSelect={() => update({ interest: 'no' })}
                    />
                    <OptionRadio
                      checked={state.interest === 'yes'}
                      label={t('이해관계 있음')}
                      onSelect={() => update({ interest: 'yes' })}
                    />
                  </BpRadioRow>
                </td>
              </tr>
            )}
            {hasParticipants && state.interest === 'yes' && (
              <tr>
                <th>
                  {t('이해관계 상세')}
                  {detailLabels.note && (
                    <>
                      <br />
                      <small>{t(detailLabels.note)}</small>
                    </>
                  )}
                </th>
                <td>
                  <BpGuideText>
                    {t('이해관계가 있는 임직원 선택 후 이해관계 입력')}
                  </BpGuideText>
                  <ApprovalFormTable
                    editable
                    className="bp-detail"
                    style={DETAIL_TABLE_STYLE}
                  >
                    <colgroup>
                      {DETAIL_COLS.map(([mobile, pc], i) => (
                        <col
                          key={i}
                          style={
                            {
                              '--bp-col': `${mobile}px`,
                              '--bp-col-pc': pc,
                            } as CSSProperties
                          }
                        />
                      ))}
                      <col className="bp-remark" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th rowSpan={2} aria-label={t('선택')} />
                        <th colSpan={3}>{t('임직원 정보')}</th>
                        <th colSpan={2}>{t('이해관계자 정보')}</th>
                        <th colSpan={2}>{t(detailLabels.contentHeader)}</th>
                      </tr>
                      <tr>
                        <th>{t('소속')}</th>
                        <th>{t('성명')}</th>
                        <th>{t('직급')}</th>
                        <th>{t('성명')}</th>
                        <th>{t('직함')}</th>
                        <th>{t('관계')}</th>
                        <th>{t('비고')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.participants.map((p) => (
                        <tr key={p.id}>
                          <td className="align-center">
                            <Checkbox
                              checked={p.checked}
                              onChange={(e) =>
                                updateParticipant(p.id, {
                                  checked: e.target.checked,
                                })
                              }
                            />
                          </td>
                          <td className="align-center">{p.department}</td>
                          <td className="align-center">{p.name}</td>
                          <td className="align-center">{p.position}</td>
                          <td>
                            <ApprovalTableCellInput
                              value={p.stakeholderName}
                              disabled={!p.checked}
                              onChange={(e) =>
                                updateParticipant(p.id, {
                                  stakeholderName: e.target.value,
                                })
                              }
                            />
                          </td>
                          <td>
                            <ApprovalTableCellInput
                              value={p.stakeholderPosition}
                              disabled={!p.checked}
                              onChange={(e) =>
                                updateParticipant(p.id, {
                                  stakeholderPosition: e.target.value,
                                })
                              }
                            />
                          </td>
                          <td>
                            <Select
                              value={p.relation}
                              disabled={!p.checked}
                              options={BP_RELATIONS.map((r) => ({
                                value: r.value,
                                label: t(r.label),
                              }))}
                              onChange={(e) =>
                                updateParticipant(p.id, {
                                  relation: e.target.value as BpRelation,
                                })
                              }
                              sx={approvalTableCellSelectSx}
                            />
                          </td>
                          <td>
                            <ApprovalTableCellInput
                              value={p.remark}
                              disabled={!p.checked}
                              placeholder={t('추가설명')}
                              onChange={(e) =>
                                updateParticipant(p.id, {
                                  remark: e.target.value,
                                })
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </ApprovalFormTable>
                </td>
              </tr>
            )}
          </tbody>
        </BpInfoTable>
      )}
    </FieldBlock>
  );
};

export default BusinessPartnerRegistrationField;
